import { useEffect, useRef } from 'react';
import { useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ToggleInput, useScroll } from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import Sheet from '../../components/profit-loss/sheet';
import { changeVisibility } from './store/slices/sheet';
import { AccountType } from '../types';
import PrecisionSelector from '../../components/partial/reports/precision-selector';

import cn from './profit-loss.module.css';

const ProfitLossSheetHOC = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { search } = useLocation();

  const {
    profitLoss: {
      sheet: {
        itemsVisible,
        requestInProgress,
        values,
        totalExpenses,
        profitLoss,
        accountsCategories,
      },
    },
  } = useAppSelector((state) => state);

  const {
    isLoading,
    errorList,
    excludeRevaluation,
    setExcludeRevaluation,
    dates,
    precision,
    setPrecision,
  } = useOutletContext<{
    isLoading: boolean;
    errorList?: string;
    excludeRevaluation: boolean;
    setExcludeRevaluation: (data: boolean) => void;
    dates?: Date[];
    precision: string;
    setPrecision: (data: string) => void;
  }>();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const onVisibleChange = (type: AccountType) => {
    let nextAccountsType = ([] as AccountType[]).concat(itemsVisible);

    if (nextAccountsType.includes(type)) {
      nextAccountsType = nextAccountsType.filter((item) => item !== type);

      if (
        type === AccountType.GROUP &&
        nextAccountsType.includes(AccountType.PRIMARY_ACCOUNT)
      ) {
        nextAccountsType = [];
      }
    } else {
      nextAccountsType.push(type);

      if (
        type === AccountType.PRIMARY_ACCOUNT &&
        !nextAccountsType.includes(AccountType.GROUP)
      ) {
        nextAccountsType.push(AccountType.GROUP);
      }
    }

    dispatch(changeVisibility(nextAccountsType));
  };

  useScroll({
    element: scrollRef,
    requestInProgress,
  });

  useEffect(() => {
    let title = 'Profit-Loss sheet';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  return (
    <div className={cn.profitLossSheet} ref={scrollRef}>
      <div className={cn.profitLossSheetVisibilityControls}>
        <div className={cn.profitLossSheetVisibilityControlsLeft}>
          <ToggleInput
            name="groups"
            title="Groups"
            checked={itemsVisible.includes(AccountType.GROUP)}
            onChange={() => onVisibleChange(AccountType.GROUP)}
            className={cn.profitLossSheetVisibilityToggle}
            disabled={Boolean(errorList)}
          />
          <ToggleInput
            name="primary"
            title="Primary Accounts"
            checked={itemsVisible.includes(AccountType.PRIMARY_ACCOUNT)}
            onChange={() => onVisibleChange(AccountType.PRIMARY_ACCOUNT)}
            className={cn.profitLossSheetVisibilityToggle}
            disabled={Boolean(errorList)}
          />
          <ToggleInput
            name="excludeRevaluation"
            title="Exclude Revaluation"
            checked={excludeRevaluation}
            onChange={(e) => {
              setExcludeRevaluation(e.target.checked);

              let link = '';

              if (e.target.checked) {
                link = `${link}excludeRevaluation=${e.target.checked}`;
              }

              if (dates && dates?.length) {
                link = `${link}${
                  e.target.checked ? '&' : ''
                }transferDateFrom=${format(new Date(dates[0]), 'yyyy-MM-dd')}`;

                if (dates?.length > 1) {
                  link = `${link}&transferDateTo=${format(
                    new Date(dates[1]),
                    'yyyy-MM-dd',
                  )}`;
                }
              }

              if (precision !== '2') {
                link = `${link}&roundDecimals=${precision}`;
              }

              navigate(`?${link.replace('?', '')}`);
            }}
            className={cn.profitLossSheetVisibilityToggle}
            disabled={Boolean(errorList)}
          />
        </div>
        <div className={cn.profitLossSheetVisibilityControlsRight}>
          <PrecisionSelector
            precision={precision}
            setPrecision={(value) => {
              setPrecision(value);

              let link = `?roundDecimals=${value}`;

              if (dates && dates?.length) {
                link = `${link}&transferDateFrom=${format(
                  new Date(dates[0]),
                  'yyyy-MM-dd',
                )}`;

                if (dates?.length > 1) {
                  link = `${link}&transferDateTo=${format(
                    new Date(dates[1]),
                    'yyyy-MM-dd',
                  )}`;
                }
              }

              if (excludeRevaluation) {
                link = `${link}&excludeRevaluation=${excludeRevaluation}`;
              }

              navigate(`?${link.replace('?', '')}`);
            }}
          />
        </div>
      </div>
      {errorList && (
        <div className={cn.reportsLoadingError}>
          <div className={cn.reportsLoadingErrorTitle}>Unable to load data</div>
          <div className={cn.reportsLoadingErrorText}>{errorList}</div>
        </div>
      )}
      {!errorList && (
        <Sheet
          items={values}
          itemsVisible={itemsVisible}
          isLoading={isLoading || !accountsCategories}
          search={search}
          totalExpenses={totalExpenses}
          profitLoss={profitLoss}
        />
      )}
    </div>
  );
};

export default ProfitLossSheetHOC;
